import { Fragment, useMemo } from 'react'
import { UnorderedList, ListItem, Text, Grid, Icon } from '@chakra-ui/react'
import { useTranslation, Trans } from 'next-i18next'

import { logEvent } from '@/lib/gtm'
import env from '@/lib/env'

import { TEXT_STYLE } from '@/features/landing/constants/FAQs'
import { Item as FAQItem } from '@/features/landing/components/FAQs'
import {
  textStyle,
  listStyle,
} from '@/features/landing/components/Carousel/Contents/style'

import type { HeroProps } from '@/features/services/templates/Hero'
import type { ServiceInfoProps } from '@/features/services/templates/ServiceInfo'
import type { TextWithImageProps } from '@/features/services/templates/TextWithImage'
import { ReactComponent as CheckIcon } from '@/svg/check-icon.svg'

import DesktopBgImage from '@/images/services/legal/banner-desktop.jpg'
import MobileBgImage from '@/images/services/legal/banner-mobile.jpg'
import InfoImage from '@/images/services/legal/info.png'
import ReasonImage from '@/images/services/legal/reason.png'
import FloatImage from '@/images/services/legal/floating.jpg'

import type { WidgetProps } from '../types'
import { TARGET_TEXT_STYLE } from './style'

export const useMeta = (): PageMeta => {
  const { t } = useTranslation('services')

  return {
    title: t('legal.meta.title'),
    description: t('legal.meta.description'),
    og: {
      title: t('legal.meta.og.title'),
      description: t('legal.meta.og.description'),
      image: {
        url: `${env.APP_URL}${t('legal.meta.og.image.url')}`,
      },
    },
  }
}

export const useHeroData = (): HeroProps => {
  const {
    t,
    i18n: { language },
  } = useTranslation('services')

  return useMemo(
    () => ({
      title: t('legal.title'),
      subtitle: t('legal.subtitle'),
      image: {
        base: `url(${MobileBgImage.src})`,
        md: `url(${DesktopBgImage.src})`,
      },
      titleColor: 'white',
      subtitleColor: 'white',
      colorSchemePrimary: 'primary',
      colorSchemeSecondary: 'white',
      bgColor: 'secondary.800',
      onClickGetQuote: (): void => {
        logEvent({
          ga: {
            category: 'Legal',
            action: 'Click get quote',
          },
        })
      },
    }),
    [language],
  )
}

export const useServiceInfoData = (): ServiceInfoProps => {
  const {
    t,
    i18n: { language },
  } = useTranslation('services')

  return useMemo(
    () => ({
      title: t('legal.serviceInfo.title'),
      paragraph: (
        <Trans t={t} i18nKey="legal.serviceInfo.paragraph">
          บริการจัดทำ ตรวจสอบ แก้ไขเอกสารทางกฎหมาย (Legal Documents)
          ที่เกี่ยวกับ PDPA เพื่อช่วยให้ธุรกิจของคุณสอดคล้องกับ PDPA
          โดยสามารถปรับแต่งตามงบประมาณที่คุณจัดสรรได้อีกด้วย
        </Trans>
      ),
      image: InfoImage,
    }),
    [language],
  )
}

export interface ReasonProps {
  title: string
  paragraph: React.ReactNode
  image: string | StaticImageData
}

export const useServiceReasonData = (): ReasonProps => {
  const {
    t,
    i18n: { language },
  } = useTranslation('services')

  return useMemo(
    () => ({
      image: ReasonImage,
      title: t('legal.reasons.title'),
      paragraph: (
        <UnorderedList sx={listStyle}>
          <ListItem>
            <Text as="h4" sx={{ fontWeight: 'medium', fontSize: 'xl' }}>
              {t('legal.reasons.answers.0.title')}
            </Text>
          </ListItem>
          <UnorderedList sx={listStyle}>
            <ListItem>{t('legal.reasons.answers.0.list.0')}</ListItem>
            <ListItem>{t('legal.reasons.answers.0.list.1')}</ListItem>
          </UnorderedList>
          <ListItem>
            <Text as="h4" sx={{ fontWeight: 'medium', fontSize: 'xl' }}>
              {t('legal.reasons.answers.1.title')}
            </Text>
          </ListItem>
          <ListItem>
            <Text as="h4" sx={{ fontWeight: 'medium', fontSize: 'xl' }}>
              {t('legal.reasons.answers.2.title')}
            </Text>
          </ListItem>
        </UnorderedList>
      ),
    }),
    [language],
  )
}

export const useDocumentData = (): WidgetProps[] => {
  const {
    t,
    i18n: { language },
  } = useTranslation('services')

  const styles = {
    fontWeight: 'medium',
    fontSize: { base: 'lg', sm: '2xl' },
    textAlign: { xs: 'center', sm: 'left' },
    paddingBottom: '8px',
  }

  const List = (value: { text: string }) => {
    return (
      <Grid
        sx={{
          gridTemplateColumns: '16px auto',
          gap: '8px',
          alignItems: 'baseline',
          fontSize: { base: 'xs', sm: 'md' },
        }}
      >
        <span>
          <Icon
            as={CheckIcon}
            sx={{
              width: { base: `13px`, sm: '18px' },
              height: { base: `13px`, sm: '18px' },
            }}
          />
        </span>

        {value.text}
      </Grid>
    )
  }
  return useMemo(
    () => [
      {
        image: '/images/services/legal/features-1.svg',
        text: (
          <Fragment>
            <Text as="h3" sx={styles}>
              {t('legal.document.contents.0.title')}
            </Text>
            <UnorderedList sx={listStyle}>
              <List text={t('legal.document.contents.0.list.0')} />
              <List text={t('legal.document.contents.0.list.1')} />
              <List text={t('legal.document.contents.0.list.2')} />
              <List text={t('legal.document.contents.0.list.3')} />
            </UnorderedList>
          </Fragment>
        ),
      },
      {
        image: '/images/services/legal/features-2.svg',
        text: (
          <Fragment>
            <Text as="h3" sx={styles}>
              {t('legal.document.contents.1.title')}
            </Text>
            <UnorderedList sx={listStyle}>
              <List text={t('legal.document.contents.1.list.0')} />
              <List text={t('legal.document.contents.1.list.1')} />
              <List text={t('legal.document.contents.1.list.2')} />
              <List text={t('legal.document.contents.1.list.3')} />
            </UnorderedList>
          </Fragment>
        ),
      },
      {
        image: '/images/services/legal/features-3.svg',
        text: (
          <Fragment>
            <Text as="h3" sx={styles}>
              {t('legal.document.contents.2.title')}
            </Text>
            <UnorderedList sx={listStyle}>
              <List text={t('legal.document.contents.2.list.0')} />
              <List text={t('legal.document.contents.2.list.1')} />
            </UnorderedList>
          </Fragment>
        ),
      },
      {
        image: '/images/services/legal/features-4.svg',
        text: (
          <Fragment>
            <Text as="h3" sx={styles}>
              {t('legal.document.contents.3.title')}
            </Text>
            <UnorderedList sx={listStyle}>
              <List text={t('legal.document.contents.3.list.0')} />
              <List text={t('legal.document.contents.3.list.1')} />
            </UnorderedList>
          </Fragment>
        ),
      },
      {
        image: '/images/services/legal/features-5.svg',
        text: (
          <Fragment>
            <Text as="h3" sx={styles}>
              {t('legal.document.contents.4.title')}
            </Text>
            <UnorderedList sx={listStyle}>
              <List text={t('legal.document.contents.4.list.0')} />
              <List text={t('legal.document.contents.4.list.1')} />
              <List text={t('legal.document.contents.4.list.2')} />
              <List text={t('legal.document.contents.4.list.3')} />
            </UnorderedList>
          </Fragment>
        ),
      },
      {
        image: '/images/services/legal/features-6.svg',
        text: (
          <Fragment>
            <Text as="h3" sx={styles}>
              {t('legal.document.contents.5.title')}
            </Text>
            <UnorderedList sx={listStyle}>
              <List text={t('legal.document.contents.5.list.0')} />
              <List text={t('legal.document.contents.5.list.1')} />
              <List text={t('legal.document.contents.5.list.2')} />
            </UnorderedList>
          </Fragment>
        ),
      },
      {
        image: '/images/services/legal/features-7.svg',
        text: (
          <Fragment>
            <Text as="h3" sx={styles}>
              {t('legal.document.contents.6.title')}
            </Text>
            <UnorderedList sx={listStyle}>
              <List text={t('legal.document.contents.6.list.0')} />
              <List text={t('legal.document.contents.6.list.1')} />
              <List text={t('legal.document.contents.6.list.2')} />
              <List text={t('legal.document.contents.6.list.3')} />
              <List text={t('legal.document.contents.6.list.4')} />
            </UnorderedList>
          </Fragment>
        ),
      },
    ],
    [language],
  )
}

export const useTargetData = (): TextWithImageProps => {
  const {
    t,
    i18n: { language },
  } = useTranslation('services')

  return useMemo(
    () => ({
      title: t('legal.target.title'),
      paragraph: (
        <Text sx={{ ...textStyle, ...TARGET_TEXT_STYLE }}>
          {t('legal.target.paragraph')}
        </Text>
      ),
      image: FloatImage,
      imageRatio: 510 / 351,
    }),
    [language],
  )
}

export const useFAQsData = (): FAQItem[] => {
  const {
    t,
    i18n: { language },
  } = useTranslation('faqs')

  return useMemo(
    () => [
      {
        title: t('FAQs.6.question'),
        content: <Text sx={TEXT_STYLE}>{t('FAQs.6.answer.paragraph')}</Text>,
      },
      {
        title: t('FAQs.5.question'),
        content: <Text sx={TEXT_STYLE}>{t('FAQs.5.answer.paragraph')}</Text>,
      },
    ],
    [language],
  )
}
